import React, { useContext, useEffect, useRef, useState } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import TaskView from './TaskView';
import { MDBIcon } from "mdb-react-ui-kit";
import { Glyph, GlyphLayer, IdeaBoxEntry } from 'konect-model';
import "./GlyphView.css";
import { DropType } from './GlyphGrouping';
import UserControlService from '../../services/user-control-service';
import { AppContext } from '../App';
import AcceptModal from './AcceptModal';
import { Settings } from '../Settings';

interface GlyphViewProps {
  index: number;
  name: string;
  glyph: Glyph;
  isDragging: any;
  onRemove: (id: string) => void;
  onDraw: (id: string) => void;
}

export default function GlyphView(props: GlyphViewProps) {
  const { project } = useContext(AppContext);
  const [name, setName] = useState<string | undefined>(props.glyph.name);
  const [collapse, setCollapse] = useState(Settings.isCollapsedView(props.glyph.id));
  const inputRef = useRef<any>();

  const editGlyph = () => {
    props.onDraw(props.glyph.id);
  }

  const removeGlyph = () => {
    props.onRemove(props.glyph.id);
  }

  const changeGlyphName = (event: any) => {
    setName(event.target.value);
    clearTimeout(inputRef.current);
    inputRef.current = setTimeout(() => {
      UserControlService.update();
      props.glyph.name = event.target.value;
    }, 500)
  }

  const toggleGlyphCollapse = () => {
    Settings.collapseView(props.glyph.id, !collapse);
    setCollapse(!collapse);
  }

  useEffect(() => {
    setName(project.glyphs.find((gly: Glyph) => gly.id === props.glyph.id)?.name);
  }, [project])  // eslint-disable-line react-hooks/exhaustive-deps

  const layers = props.glyph.layers;

  return <Draggable draggableId={props.glyph.id} index={props.index}>
    {provided => (
      <div className={'glyph-container' + (collapse ? " hide" : "")}
        {...provided.draggableProps}
        ref={provided.innerRef}
      >
        <div style={{ borderRadius: '10px' }} className="border bg-light overflow-auto">

          <div className='glyph-title' {...provided.dragHandleProps}>
            <span className="d-flex accept-modal-button" onClick={toggleGlyphCollapse} style={{borderColor: "transparent"}}>
            {collapse ? <MDBIcon fas icon='caret-right' style={{transform: "translateX(25%) scale(1.5)"}}/> : <MDBIcon fas icon='caret-down' style={{transform: "scale(1.5)"}}/>}
            </span>
            <span className="d-flex align-items-center justify-content-end">
            <input
              className={'name-input'}
              style={name ? name.length > 18 ? {width: "calc(3rem + " + name?.length + "ch)"} : {} : { fontStyle: "italic" }}
              type="text"
              placeholder={"Unnamed Glyph"}
              name="glyphName"
              value={name}
              onChange={changeGlyphName}
            />
              <span className='accept-modal-button justify-content-center draw' onClick={editGlyph} >
                <MDBIcon fas icon='brush' />
              </span>
            </span>
            <AcceptModal imgType={"trash"} onAccept={removeGlyph} />
          </div>

          {layers.length <= 1 && !collapse && <div className={"glyph-tip"}>Add/sort tasks by dragging them here!</div>}

          <Droppable droppableId={props.glyph.id} type={DropType.LAYER} direction="horizontal" isDropDisabled={layers.length <= 2}>
            {(provided, snapshot) =>
              <div className='layer-container'
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {!collapse && layers.map((layer, index) => {
                  const e = props.isDragging;
                  const show = e && e.type === DropType.TASK &&
                    (e.source.droppableId.includes("backlog") || index === 0 || index < layers.length - 1 || layers[index - 1].entries.length > 1 ||
                      !layers[index - 1].entries.find((el: IdeaBoxEntry) => el.id === e.draggableId))
                  return <LayerComponent
                    key={layer.id}
                    layer={layer}
                    index={index}
                    show={show}
                    weight={1 - (1 / layers.length) * index}
                  />
                })}
                {provided.placeholder}
              </div>
            }
          </Droppable>
        </div>
      </div>
    )}
  </Draggable>
}

interface LayerComponentProps {
  layer: GlyphLayer;
  index: number;
  show: boolean;
  weight: number
}

function LayerComponent(props: LayerComponentProps) {
  const layer = props.layer;
  const hasEntries = layer.entries.length > 0;

  return <Draggable draggableId={"glyph-layer" + layer.id} index={props.index} isDragDisabled={!hasEntries}>
    {(provided, snaps) => (
      <div
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        ref={provided.innerRef}
      >
        <div style={{ opacity: hasEntries || props.show ? 1 : 0, transition: "opacity 0.2s" }}>
          <div className='glyph-layer-handle'>
            <MDBIcon fas size='lg' icon="bars" />
          </div>
          <Droppable droppableId={"glyph-layer" + layer.id} type={DropType.TASK} direction="vertical" isDropDisabled={!props.show}>
            {(provided, snapshot) =>
              <div className='glyph-layer'
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <div>
                  {layer.entries.map((task, index) => (
                    <TaskView key={task.id} entry={task} index={index} />
                  ))}
                  {provided.placeholder}
                </div>
              </div>

            }
          </Droppable>
        </div>
      </div>
    )}
  </Draggable>
}